
import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag, Tag, Check } from "lucide-react";
import { Link } from "react-router";
import { useCart, type AppliedCoupon } from "@/lib/cart";
import { api, ApiError } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";

function formatPrice(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function CouponInput() {
  const { coupon, applyCoupon, removeCoupon, subtotal } = useCart();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleApply() {
    const trimmed = code.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.post<{
        code: string;
        type: AppliedCoupon["type"];
        value: number;
        description: string;
        minPurchaseCents?: number;
      }>("/api/coupons/validate", { code: trimmed });
      if (data.minPurchaseCents && subtotal < data.minPurchaseCents) {
        setError(`Minimum purchase of ${formatPrice(data.minPurchaseCents)} required`);
        return;
      }
      applyCoupon({
        code: data.code,
        type: data.type,
        value: data.value,
        description: data.description,
      } as AppliedCoupon);
      setCode("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to apply coupon");
    } finally {
      setLoading(false);
    }
  }

  if (coupon) {
    return (
      <div
        className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 mb-3"
        style={{ backgroundColor: `${colors.forestGreen}10`, border: `1px solid ${colors.forestGreen}30` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Check size={14} style={{ color: colors.forestGreen, flexShrink: 0 }} />
          <span
            className="text-xs font-bold truncate"
            style={{ fontFamily: fonts.body, color: colors.forestGreen }}
          >
            {coupon.code}
          </span>
          <span
            className="text-xs"
            style={{ fontFamily: fonts.body, fontWeight: 500, color: `${colors.navy}88` }}
          >
            {coupon.description}
          </span>
        </div>
        <button
          onClick={removeCoupon}
          className="p-1 cursor-pointer rounded-full hover:bg-red-50 transition-colors flex-shrink-0"
          style={{ color: `${colors.navy}66` }}
          aria-label="Remove coupon"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <div className="flex gap-2">
        <div
          className="flex items-center gap-2 flex-1 rounded-full border px-3"
          style={{ borderColor: `${colors.tan}40`, backgroundColor: "white" }}
        >
          <Tag size={14} style={{ color: `${colors.navy}55`, flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Coupon code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleApply()}
            className="flex-1 py-2 text-xs outline-none bg-transparent"
            style={{
              fontFamily: fonts.body,
              fontWeight: 600,
              color: colors.navy,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          />
        </div>
        <button
          onClick={handleApply}
          disabled={loading || !code.trim()}
          className="px-4 py-2 rounded-full text-xs text-white cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            fontFamily: fonts.button,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            backgroundColor: colors.navy,
          }}
        >
          {loading ? "..." : "Apply"}
        </button>
      </div>
      {error && (
        <p
          className="text-xs mt-1.5 ml-1"
          style={{ fontFamily: fonts.body, fontWeight: 600, color: "#dc2626" }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

export default function CartDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { items, removeItem, updateQuantity, coupon, subtotal, discount, totalItems, totalPrice } =
    useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCheckout() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.post<{ url: string }>("/api/checkout", {
        items: items.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
        })),
        couponCode: coupon?.code || null,
      });
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
      setLoading(false);
    }
  }

  // Rendered through a portal to document.body so the fixed-position overlay and
  // drawer size against the viewport. The Navbar this mounts under uses
  // backdrop-blur (a backdrop-filter), which would otherwise make the nav the
  // containing block for these fixed children and collapse them to its height.
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/30"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md flex flex-col shadow-2xl"
            style={{ backgroundColor: colors.cream }}
          >
            <div
              className="flex items-center justify-between px-6 py-5 border-b"
              style={{ borderColor: `${colors.tan}20` }}
            >
              <h2
                className="text-xl"
                style={{
                  fontFamily: fonts.logo,
                  fontWeight: 400,
                  color: colors.navy,
                }}
              >
                Your Cart{" "}
                {totalItems > 0 && (
                  <span
                    className="text-sm"
                    style={{
                      fontFamily: fonts.body,
                      fontWeight: 600,
                      color: `${colors.navy}88`,
                    }}
                  >
                    ({totalItems})
                  </span>
                )}
              </h2>
              <button
                onClick={onClose}
                className="p-2 cursor-pointer"
                style={{ color: colors.navy }}
                aria-label="Close cart"
              >
                <X size={22} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
                <ShoppingBag size={48} style={{ color: `${colors.navy}33` }} />
                <p
                  className="text-base"
                  style={{ fontFamily: fonts.body, fontWeight: 600, color: `${colors.navy}88` }}
                >
                  Your cart is empty
                </p>
                <Link
                  to="/shop"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-full text-sm text-white"
                  style={{
                    fontFamily: fonts.button,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    backgroundColor: colors.berryPink,
                  }}
                >
                  Browse Shop
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="flex flex-col gap-4">
                    {items.map((item) => (
                      <div
                        key={item.productId}
                        className="flex gap-4 rounded-xl p-3 border"
                        style={{ backgroundColor: "white", borderColor: `${colors.tan}20` }}
                      >
                        <div
                          className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0"
                          style={{ backgroundColor: `${colors.tan}20` }}
                        >
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ShoppingBag size={24} style={{ color: `${colors.navy}33` }} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-[10px] tracking-[0.15em] uppercase"
                            style={{ fontFamily: fonts.body, fontWeight: 700, color: `${colors.navy}66` }}
                          >
                            {item.brand}
                          </p>
                          <p
                            className="text-sm truncate"
                            style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}
                          >
                            {item.name}
                          </p>
                          <p
                            className="text-sm mt-1"
                            style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.berryPink }}
                          >
                            {formatPrice(item.price)}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                              className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer border"
                              style={{ borderColor: `${colors.tan}40`, color: colors.navy }}
                              aria-label="Decrease quantity"
                            >
                              <Minus size={14} />
                            </button>
                            <span
                              className="text-sm w-6 text-center"
                              style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}
                            >
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                              className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer border"
                              style={{ borderColor: `${colors.tan}40`, color: colors.navy }}
                              aria-label="Increase quantity"
                            >
                              <Plus size={14} />
                            </button>
                            <button
                              onClick={() => removeItem(item.productId)}
                              className="ml-auto p-1.5 cursor-pointer rounded-full hover:bg-red-50 transition-colors"
                              style={{ color: `${colors.navy}66` }}
                              aria-label="Remove item"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className="px-6 py-5 border-t"
                  style={{ borderColor: `${colors.tan}20` }}
                >
                  <CouponInput />

                  <div className="flex items-center justify-between mb-1">
                    <span
                      className="text-sm"
                      style={{ fontFamily: fonts.body, fontWeight: 600, color: `${colors.navy}88` }}
                    >
                      Subtotal
                    </span>
                    <span
                      className="text-sm"
                      style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}
                    >
                      {formatPrice(subtotal)}
                    </span>
                  </div>

                  {discount > 0 && (
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className="text-sm"
                        style={{ fontFamily: fonts.body, fontWeight: 600, color: colors.forestGreen }}
                      >
                        Discount
                      </span>
                      <span
                        className="text-sm"
                        style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.forestGreen }}
                      >
                        -{formatPrice(discount)}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-4 pt-2 border-t" style={{ borderColor: `${colors.tan}15` }}>
                    <span
                      className="text-base"
                      style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}
                    >
                      Total
                    </span>
                    <span
                      className="text-lg"
                      style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}
                    >
                      {formatPrice(totalPrice)}
                    </span>
                  </div>

                  {error && (
                    <p
                      className="text-sm mb-3 text-center"
                      style={{ fontFamily: fonts.body, fontWeight: 600, color: "#dc2626" }}
                    >
                      {error}
                    </p>
                  )}
                  <button
                    onClick={handleCheckout}
                    disabled={loading}
                    className="w-full py-3.5 rounded-full text-sm text-white cursor-pointer transition-all duration-200 hover:shadow-lg disabled:opacity-60 disabled:cursor-wait"
                    style={{
                      fontFamily: fonts.button,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      backgroundColor: colors.berryPink,
                    }}
                  >
                    {loading ? "Redirecting..." : "Checkout"}
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
